import React from 'react';
import { useFieldArray } from 'react-final-form-arrays';
import PropTypes from 'prop-types';
import { useForm } from 'react-final-form';
import Stack from '@mui/material/Stack';
import { SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { closestCorners, DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import Box from '@mui/material/Box';
import MediaAttachment from './MediaAttachment';

const DraggableItem = ({ itemId, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: itemId });
  const style = {
    cursor: 'move',
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <Box ref={setNodeRef} sx={style} {...attributes} {...listeners}>
      {children}
    </Box>
  );
};

DraggableItem.propTypes = {
  itemId: PropTypes.any.isRequired,
};

// Ignore all arguments
const NoArgsReactFragment = ({ children }) => <>{children}</>;

const MediaExhibit = ({ name = 'media', orderable = false }) => {
  const form = useForm();
  const { fields } = useFieldArray(name);

  const getItemId = (value) => value?.id ?? value?.$id;

  const findItemById = (uniqueId) => {
    const itemIndex = form.getFieldState(name)?.value?.findIndex(item => getItemId(item) === uniqueId);
    const itemValue = form.getFieldState(name)?.value?.[itemIndex];

    return [itemIndex, itemValue];
  };

  const onUpdateGenerator = (uniqueId) => (newValue) => {
    const [index] = findItemById(uniqueId);

    fields.update(index, newValue);
  };

  const onRemoveGenerator = (uniqueId) => () => {
    const [index, value] = findItemById(uniqueId);

    if (value.id) {
      fields.update(index, { ...value, delete: true }); // Already stored files
    } else {
      fields.remove(index); // New files
    }
  };

  const handleDragEnd = event => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const [activeIndex] = findItemById(active.id);
      const [overIndex] = findItemById(over.id);

      fields.move(activeIndex, overIndex);
    }
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      activationConstraint: { distance: 5 },
    }),
  );

  const visibleItemsLength = fields.value?.filter((item) => !item?.delete)?.length ?? 0;

  const MaybeDndContext = orderable && visibleItemsLength > 1 ? DndContext : NoArgsReactFragment;
  const MaybeSortableContext = orderable && visibleItemsLength > 1 ? SortableContext : NoArgsReactFragment;
  const MaybeDraggableItem = orderable && visibleItemsLength > 1 ? DraggableItem : NoArgsReactFragment;

  if (visibleItemsLength === 0) {
    return null;
  }

  return (
    <Stack spacing={2} useFlexGap direction="row" alignItems="flex-start" flexWrap="wrap">
      <MaybeDndContext collisionDetection={closestCorners} onDragEnd={handleDragEnd} sensors={sensors}>
        <MaybeSortableContext items={fields.value.map(getItemId)}>
          {fields
            .map((fieldName, idx) => ({
              fieldName,
              itemId: getItemId(fields.value[idx]),
              itemValue: fields.value[idx],
            }))
            .filter(({ itemValue }) => !itemValue?.delete)
            .map(({ fieldName, itemId, itemValue }) => (
              <MaybeDraggableItem key={itemId} itemId={itemId}>
                <MediaAttachment
                  fieldName={fieldName}
                  value={itemValue}

                  onUpdate={onUpdateGenerator(itemId)}
                  onRemove={onRemoveGenerator(itemId)}
                />
              </MaybeDraggableItem>
            ))}
        </MaybeSortableContext>
      </MaybeDndContext>
    </Stack>
  );
};

MediaExhibit.propTypes = {
  name: PropTypes.string,
  orderable: PropTypes.bool,
};

export default MediaExhibit;
